import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Plus, Search, Edit, Trash2, X, Printer, FileText, Pencil, RefreshCw } from 'lucide-react';
import ExportButton from '../components/ExportButton';
import PDFExportButton from '../components/PDFExportButton';
import { QuickCreateCustomer, QuickCreateProduct } from '../components/QuickCreateModals';
import { toast } from 'sonner';

const SalesOrders = () => {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusForm, setStatusForm] = useState({ status: '', notes: '' });
  const [showQuickCustomer, setShowQuickCustomer] = useState(false);
  const [showQuickProduct, setShowQuickProduct] = useState(false);
  const [showCoaModal, setShowCoaModal] = useState(false);
  const [coaOrder, setCoaOrder] = useState(null);
  const [users, setUsers] = useState([]);
  const [coaForm, setCoaForm] = useState({ customer_id: '', customer_name: '', customer_address: '', sku: '', product_name: '', product_size: '', item_code: '', batch_number: '', mfg_date: '', expiry_date: '', quantity_text: '', prepared_by: '', approved_by: '', test_rows: [{ sno: 1, test: '', specification: '', result: '' }] });
  const [coaVersionInfo, setCoaVersionInfo] = useState(null);
  const NOTES_MAX = 1000;
  const emptyAddress = { address_line1: '', address_line2: '', city: '', state: '', pincode: '', country: 'India' };
  const [formData, setFormData] = useState({
    customer_id: '',
    po_number: '',
    po_date: '',
    billing_address: null,
    shipping_address: null,
    notes: '',
    items: [{ sku: '', name: '', item_code: '', hsn_code: '', quantity: 0, unit: 'Nos', unit_price: 0, gst_rate: 18 }]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, customersRes, inventoryRes, usersRes] = await Promise.all([
        api.get('/sales/orders'),
        api.get('/purchase/customers'),
        api.get('/sales/inventory'),
        api.get('/admin/users')
      ]);
      setOrders(ordersRes.data);
      setCustomers(customersRes.data);
      setInventory(inventoryRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectCustomer = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setFormData({
        ...formData,
        customer_id: customerId,
        billing_address: { ...emptyAddress, ...(customer.billing_address || {}) },
        shipping_address: { ...emptyAddress, ...(customer.shipping_address || customer.billing_address || {}) }
      });
    }
  };

  const updateAddress = (type, field, value) => {
    setFormData(prev => ({
      ...prev,
      [type]: { ...(prev[type] || emptyAddress), [field]: value }
    }));
  };

  const copyBillingToShipping = () => {
    setFormData(prev => ({ ...prev, shipping_address: { ...(prev.billing_address || emptyAddress) } }));
  };

  const blankForm = () => ({
    customer_id: '', po_number: '', po_date: '', billing_address: null, shipping_address: null, notes: '',
    items: [{ sku: '', name: '', item_code: '', hsn_code: '', quantity: 0, unit: 'Nos', unit_price: 0, gst_rate: 18 }]
  });

  const openCreateOrder = () => { setEditingId(null); setFormData(blankForm()); setShowModal(true); };

  const openEditOrder = (order) => {
    setEditingId(order.id);
    setFormData({
      customer_id: order.customer_id || '',
      po_number: order.po_number || '',
      po_date: order.po_date || '',
      billing_address: order.billing_address ? { ...emptyAddress, ...order.billing_address } : { ...emptyAddress },
      shipping_address: order.shipping_address ? { ...emptyAddress, ...order.shipping_address } : { ...emptyAddress },
      notes: order.notes || '',
      items: (order.items && order.items.length > 0)
        ? order.items.map(i => ({ sku: i.sku, name: i.name, item_code: i.item_code || '', hsn_code: i.hsn_code || '', quantity: i.quantity, unit: i.unit || 'Nos', unit_price: i.unit_price, gst_rate: i.gst_rate }))
        : [{ sku: '', name: '', item_code: '', hsn_code: '', quantity: 0, unit: 'Nos', unit_price: 0, gst_rate: 18 }]
    });
    setShowModal(true);
  };

  const closeOrderModal = () => { setShowModal(false); setEditingId(null); };

  const calculateItemTotals = (item) => {
    const taxable = item.quantity * item.unit_price;
    const gstAmount = (taxable * item.gst_rate) / 100;
    const cgst = gstAmount / 2;
    const sgst = gstAmount / 2;
    const total = taxable + gstAmount;
    return { ...item, taxable_amount: taxable, cgst, sgst, igst: 0, total_amount: total, allocated_quantity: 0, production_quantity: 0 };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const itemsWithTotals = formData.items.map(calculateItemTotals);
      if (editingId) {
        await api.put(`/sales/orders/${editingId}`, {
          customer_id: formData.customer_id,
          po_number: formData.po_number,
          po_date: formData.po_date,
          billing_address: formData.billing_address,
          shipping_address: formData.shipping_address,
          notes: formData.notes,
          items: itemsWithTotals
        });
        toast.success('Sales order updated');
      } else {
        const res = await api.post('/sales/orders', { ...formData, items: itemsWithTotals });
        // Auto-submit for workflow approval
        try {
          await api.post('/workflow/approvals/request', {
            document_type: 'sales_order', document_id: res.data.id,
            document_number: res.data.order_number, module: 'sales', max_level: 3
          });
        } catch (wfErr) { /* Workflow optional */ }
      }
      setShowModal(false);
      setEditingId(null);
      setFormData(blankForm());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (editingId ? 'Failed to update order' : 'Failed to create order'));
    }
  };

  const openStatusModal = (order) => {
    setEditingOrder(order);
    setStatusForm({ status: order.status, notes: order.notes || '' });
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/sales/orders/${editingOrder.id}`, statusForm);
      setShowStatusModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    }
  };

  const deleteOrder = async (id, orderNumber) => {
    if (!window.confirm(`Are you sure you want to delete order "${orderNumber}"?`)) return;
    try {
      await api.delete(`/sales/orders/${id}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { sku: '', name: '', item_code: '', hsn_code: '', quantity: 0, unit: 'Nos', unit_price: 0, gst_rate: 18 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length <= 1) return;
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    if (field === 'sku' && value) {
      const invItem = inventory.find(i => i.sku === value);
      if (invItem) {
        newItems[index].name = invItem.name;
        newItems[index].item_code = invItem.sku || '';
        newItems[index].hsn_code = invItem.hsn_code || '';
        newItems[index].unit = invItem.unit || 'Nos';
        newItems[index].unit_price = invItem.unit_price || 0;
        newItems[index].gst_rate = invItem.gst_rate || 18;
      }
    }
    setFormData({ ...formData, items: newItems });
  };

  const calculateOrderTotals = () => {
    let subtotal = 0, cgstTotal = 0, sgstTotal = 0, igstTotal = 0;
    formData.items.forEach(item => {
      const c = calculateItemTotals(item);
      subtotal += c.taxable_amount;
      cgstTotal += c.cgst;
      sgstTotal += c.sgst;
      igstTotal += c.igst;
    });
    return { subtotal, cgstTotal, sgstTotal, igstTotal, total: subtotal + cgstTotal + sgstTotal + igstTotal };
  };

  const totals = calculateOrderTotals();

  const openCoaForOrder = async (order) => {
    setCoaOrder(order);
    const cust = customers.find(c => c.name === order.customer_name);
    const firstItem = order.items?.[0];
    const custId = cust?.id || '';
    const sku = firstItem?.sku || '';
    setCoaForm({
      customer_id: custId,
      customer_name: order.customer_name || '',
      customer_address: cust ? `${cust.name}\n${typeof cust.address === 'string' ? cust.address : cust.address?.address_line1 || ''}`.trim() : order.customer_name || '',
      sku: sku,
      product_name: firstItem?.name || '',
      product_size: '', item_code: sku,
      batch_number: '', mfg_date: '', expiry_date: '',
      quantity_text: firstItem ? `${firstItem.quantity} ${firstItem.unit || 'pcs'}` : '',
      prepared_by: '', approved_by: '',
      test_rows: [{ sno: 1, test: '', specification: '', result: '' }]
    });
    // Check for existing version
    if (custId && sku) {
      try {
        const r = await api.get(`/sales/coa/latest-version?customer_id=${custId}&sku=${sku}`);
        setCoaVersionInfo(r.data);
        if (r.data.found && r.data.data) {
          const d = r.data.data;
          setCoaForm(prev => ({
            ...prev,
            product_name: d.product_name || prev.product_name,
            product_size: d.product_size || prev.product_size,
            item_code: d.item_code || prev.item_code,
            test_rows: d.test_rows?.length > 0 ? d.test_rows : prev.test_rows,
            prepared_by: d.prepared_by || '', approved_by: d.approved_by || '',
          }));
        }
      } catch { setCoaVersionInfo(null); }
    } else { setCoaVersionInfo(null); }
    setShowCoaModal(true);
  };

  const handleCoaSkuChange = async (sku) => {
    const item = inventory.find(i => i.sku === sku);
    setCoaForm(prev => ({ ...prev, sku, product_name: item?.name || '', item_code: sku }));
    if (coaForm.customer_id && sku) {
      try {
        const r = await api.get(`/sales/coa/latest-version?customer_id=${coaForm.customer_id}&sku=${sku}`);
        setCoaVersionInfo(r.data);
        if (r.data.found && r.data.data) {
          const d = r.data.data;
          setCoaForm(prev => ({ ...prev, product_size: d.product_size || prev.product_size, item_code: d.item_code || prev.item_code, test_rows: d.test_rows?.length > 0 ? d.test_rows : prev.test_rows, prepared_by: d.prepared_by || '', approved_by: d.approved_by || '' }));
        }
      } catch { setCoaVersionInfo(null); }
    }
  };

  const addCoaTestRow = () => setCoaForm(prev => ({ ...prev, test_rows: [...prev.test_rows, { sno: prev.test_rows.length + 1, test: '', specification: '', result: '' }] }));
  const removeCoaTestRow = (idx) => setCoaForm(prev => ({ ...prev, test_rows: prev.test_rows.filter((_, i) => i !== idx).map((r, i) => ({ ...r, sno: i + 1 })) }));
  const updateCoaTestRow = (idx, field, val) => setCoaForm(prev => { const rows = [...prev.test_rows]; rows[idx] = { ...rows[idx], [field]: val }; return { ...prev, test_rows: rows }; });

  const submitCoaFromOrder = async (e) => {
    e.preventDefault();
    try {
      await api.post('/sales/coa', coaForm);
      setShowCoaModal(false);
      toast.error('CoA created successfully!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create CoA'); }
  };

  const printOrder = (order) => {
    const printWin = window.open('', '_blank');
    const items = order.items || [];
    printWin.document.write(`
      <html><head><title>Sales Order - ${order.order_number}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:32px;font-size:13px;color:#222}
        h1{margin:0;font-size:22px}h2{margin:0;font-size:14px;font-weight:400;color:#666}
        .header{display:flex;justify-content:space-between;border-bottom:2px solid #000;padding-bottom:16px;margin-bottom:16px}
        .section{margin-bottom:16px}
        .section-title{font-weight:700;font-size:12px;text-transform:uppercase;color:#666;margin-bottom:4px}
        .addr{line-height:1.6}
        table{width:100%;border-collapse:collapse;margin:16px 0}
        th,td{border:1px solid #ccc;padding:8px 10px;text-align:left;font-size:12px}
        th{background:#f0f0f0;font-weight:600}
        td.num{text-align:right;font-variant-numeric:tabular-nums}
        .totals{float:right;width:280px;margin-top:16px}
        .totals .row{display:flex;justify-content:space-between;padding:4px 0;font-size:13px}
        .totals .row.total{border-top:2px solid #000;font-weight:700;font-size:15px;padding-top:8px;margin-top:4px}
        @media print{button{display:none}}
      </style></head><body>
      <div class="header">
        <div><h1>Sales Order</h1><h2>${order.order_number}</h2></div>
        <div style="text-align:right"><div style="font-size:14px;font-weight:700">${order.customer_name}</div>
        <div>${order.customer_gstin || 'No GSTIN'}</div>
        <div>Date: ${new Date(order.created_at).toLocaleDateString()}</div>
        ${order.po_number ? `<div>PO Number: ${order.po_number}</div>` : ''}
        ${order.po_date ? `<div>PO Date: ${order.po_date}</div>` : ''}
        <div>Status: ${order.status.toUpperCase()}</div></div>
      </div>
      ${order.billing_address ? `
      <div style="display:flex;gap:32px;margin-bottom:16px">
        <div class="section" style="flex:1"><div class="section-title">Billing Address</div>
        <div class="addr">${order.billing_address.address_line1}<br>${order.billing_address.address_line2 ? order.billing_address.address_line2 + '<br>' : ''}${order.billing_address.city}, ${order.billing_address.state} - ${order.billing_address.pincode}</div></div>
        <div class="section" style="flex:1"><div class="section-title">Shipping Address</div>
        <div class="addr">${order.shipping_address?.address_line1 || ''}<br>${order.shipping_address?.address_line2 ? order.shipping_address.address_line2 + '<br>' : ''}${order.shipping_address?.city || ''}, ${order.shipping_address?.state || ''} - ${order.shipping_address?.pincode || ''}</div></div>
      </div>` : ''}
      <table><thead><tr><th>#</th><th>Structure</th><th>Product Name</th><th>Item Code</th><th>HSN</th><th>Qty</th><th>Unit</th><th>Rate</th><th>Taxable</th><th>CGST</th><th>SGST</th><th>Total</th></tr></thead><tbody>
      ${items.map((i, idx) => `<tr><td>${idx + 1}</td><td>${i.sku}</td><td>${i.name}</td><td>${i.item_code || '-'}</td><td>${i.hsn_code || '-'}</td>
      <td class="num">${i.quantity}</td><td>${i.unit || '-'}</td><td class="num">${(i.unit_price || 0).toFixed(2)}</td>
      <td class="num">${(i.taxable_amount || 0).toFixed(2)}</td><td class="num">${(i.cgst || 0).toFixed(2)}</td>
      <td class="num">${(i.sgst || 0).toFixed(2)}</td><td class="num">${(i.total_amount || 0).toFixed(2)}</td></tr>`).join('')}
      </tbody></table>
      <div class="totals">
        <div class="row"><span>Subtotal:</span><span>${(order.subtotal || 0).toFixed(2)}</span></div>
        <div class="row"><span>CGST:</span><span>${(order.cgst_total || 0).toFixed(2)}</span></div>
        <div class="row"><span>SGST:</span><span>${(order.sgst_total || 0).toFixed(2)}</span></div>
        ${(order.igst_total || 0) > 0 ? `<div class="row"><span>IGST:</span><span>${order.igst_total.toFixed(2)}</span></div>` : ''}
        <div class="row total"><span>Total Amount:</span><span>INR ${(order.total_amount || 0).toFixed(2)}</span></div>
      </div>
      <div style="clear:both"></div>
      ${order.notes ? `<div class="section" style="margin-top:24px"><div class="section-title">Notes</div><div>${order.notes}</div></div>` : ''}
      <button onclick="window.print()" style="margin-top:24px;padding:8px 24px;cursor:pointer">Print</button>
      </body></html>`);
    printWin.document.close();
  };

  const getStatusBadge = (status) => {
    const badges = { pending: 'badge-warning', allocated: 'badge-success', partial: 'badge-info', production: 'badge-info', completed: 'badge-success', cancelled: 'badge-danger' };
    return `badge ${badges[status] || 'badge-info'}`;
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch =
      order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.customer_gstin && order.customer_gstin.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div data-testid="sales-orders-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Sales Orders</h1>
        <div className="flex gap-2">
          <ExportButton endpoint="/export/sales-orders" filename="sales_orders_export.xlsx" />
          <PDFExportButton endpoint="/export/pdf/sales-orders" filename="sales_orders_export.pdf" />
          <button onClick={openCreateOrder} className="btn btn-primary" data-testid="create-order-btn">
            <Plus size={16} />
            Create Order
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="grid grid-cols-2" style={{ gap: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
              <input
                type="text"
                className="form-input"
                placeholder="Search by order #, customer, or GSTIN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '40px' }}
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter">
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="allocated">Allocated</option>
              <option value="partial">Partial</option>
              <option value="production">Production</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div data-testid="loading-indicator">Loading orders...</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th className="text-right">Amount</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <tr key={order.id} data-testid={`order-row-${order.order_number}`}>
                    <td className="font-mono">{order.order_number}</td>
                    <td>
                      <div>{order.customer_name}</div>
                      <div className="text-xs text-muted">{order.customer_gstin || 'No GSTIN'}</div>
                    </td>
                    <td>{order.items?.length || 0} items</td>
                    <td className="text-right tabular-nums">{order.total_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, style: 'currency', currency: 'INR' })}</td>
                    <td>
                      <span className={getStatusBadge(order.status)} data-testid={`status-${order.order_number}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="text-sm">{new Date(order.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => printOrder(order)} className="btn btn-ghost btn-sm" title="Print Order" data-testid={`print-btn-${order.order_number}`}>
                          <Printer size={14} />
                        </button>
                        <button onClick={() => openCoaForOrder(order)} className="btn btn-ghost btn-sm" title="Create CoA" data-testid={`coa-btn-${order.order_number}`}>
                          <FileText size={14} color="#8b5cf6" />
                        </button>
                        {['pending', 'allocated', 'partial'].includes(order.status) && (
                          <button onClick={() => openEditOrder(order)} className="btn btn-ghost btn-sm" title="Edit Order" data-testid={`edit-order-btn-${order.order_number}`}>
                            <Pencil size={14} />
                          </button>
                        )}
                        <button onClick={() => openStatusModal(order)} className="btn btn-ghost btn-sm" title="Update Status" data-testid={`edit-btn-${order.order_number}`}>
                          <RefreshCw size={14} />
                        </button>
                        {(order.status === 'pending' || order.status === 'cancelled') && (
                          <button onClick={() => deleteOrder(order.id, order.order_number)} className="btn btn-ghost btn-sm" title="Delete" data-testid={`delete-btn-${order.order_number}`}>
                            <Trash2 size={14} color="#DC2626" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Status Update Modal */}
      {showStatusModal && editingOrder && (
        <div className="modal-overlay" data-testid="status-modal">
          <div className="modal-content" style={{ maxWidth: '450px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Update Order {editingOrder.order_number}</h2>
            </div>
            <form onSubmit={handleStatusUpdate}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={statusForm.status} onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })} data-testid="status-select">
                    <option value="pending">Pending</option>
                    <option value="allocated">Allocated</option>
                    <option value="partial">Partial</option>
                    <option value="production">Production</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" value={statusForm.notes} maxLength={NOTES_MAX} onChange={(e) => setStatusForm({ ...statusForm, notes: e.target.value.slice(0, NOTES_MAX) })} data-testid="status-notes-input" />
                  <div style={{ textAlign: 'right', fontSize: '11px', color: 'var(--text-tertiary)' }}>{statusForm.notes.length} / {NOTES_MAX}</div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowStatusModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="update-status-btn">Update</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Order Modal */}
      {showModal && (
        <div className="modal-overlay" data-testid="order-modal">
          <div className="modal-content" style={{ maxWidth: '1000px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editingId ? 'Edit Sales Order' : 'Create Sales Order'}</h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <div className="flex items-center justify-between" style={{ marginBottom: '4px' }}>
                    <label className="form-label" style={{ margin: 0 }}>Customer *</label>
                    <button type="button" onClick={() => setShowQuickCustomer(true)} className="btn btn-ghost btn-sm" style={{ fontSize: '11px', padding: '2px 8px' }} data-testid="quick-add-customer"><Plus size={12} /> New</button>
                  </div>
                  <select
                    className="form-select"
                    value={formData.customer_id}
                    onChange={(e) => selectCustomer(e.target.value)}
                    required
                    data-testid="customer-select"
                  >
                    <option value="">Select customer...</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>{customer.name} - {customer.customer_code}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2" style={{ gap: '16px' }}>
                  <div className="form-group">
                    <label className="form-label">Customer PO Number</label>
                    <input type="text" className="form-input" value={formData.po_number} onChange={(e) => setFormData({ ...formData, po_number: e.target.value })} placeholder="e.g. PO-12345" data-testid="po-number-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">PO Date</label>
                    <input type="date" className="form-input" value={formData.po_date} onChange={(e) => setFormData({ ...formData, po_date: e.target.value })} data-testid="po-date-input" />
                  </div>
                </div>

                {formData.billing_address && (
                  <div className="grid grid-cols-2" style={{ marginBottom: '16px', gap: '16px' }}>
                    <div style={{ padding: '12px', backgroundColor: 'var(--bg-surface-alt)', borderRadius: '4px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>Billing Address</div>
                      <input type="text" className="form-input" style={{ marginBottom: '6px', fontSize: '13px' }} placeholder="Address line 1" value={formData.billing_address.address_line1 || ''} onChange={(e) => updateAddress('billing_address', 'address_line1', e.target.value)} data-testid="billing-line1" />
                      <input type="text" className="form-input" style={{ marginBottom: '6px', fontSize: '13px' }} placeholder="Address line 2" value={formData.billing_address.address_line2 || ''} onChange={(e) => updateAddress('billing_address', 'address_line2', e.target.value)} />
                      <div className="grid grid-cols-3" style={{ gap: '6px' }}>
                        <input type="text" className="form-input" style={{ fontSize: '13px' }} placeholder="City" value={formData.billing_address.city || ''} onChange={(e) => updateAddress('billing_address', 'city', e.target.value)} />
                        <input type="text" className="form-input" style={{ fontSize: '13px' }} placeholder="State" value={formData.billing_address.state || ''} onChange={(e) => updateAddress('billing_address', 'state', e.target.value)} />
                        <input type="text" className="form-input" style={{ fontSize: '13px' }} placeholder="Pincode" value={formData.billing_address.pincode || ''} onChange={(e) => updateAddress('billing_address', 'pincode', e.target.value)} />
                      </div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: 'var(--bg-surface-alt)', borderRadius: '4px' }}>
                      <div className="flex items-center justify-between" style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600 }}>Shipping Address</div>
                        <button type="button" onClick={copyBillingToShipping} className="btn btn-ghost btn-sm" style={{ fontSize: '11px', padding: '2px 6px' }} data-testid="copy-billing-btn">Same as billing</button>
                      </div>
                      <input type="text" className="form-input" style={{ marginBottom: '6px', fontSize: '13px' }} placeholder="Address line 1" value={formData.shipping_address?.address_line1 || ''} onChange={(e) => updateAddress('shipping_address', 'address_line1', e.target.value)} data-testid="shipping-line1" />
                      <input type="text" className="form-input" style={{ marginBottom: '6px', fontSize: '13px' }} placeholder="Address line 2" value={formData.shipping_address?.address_line2 || ''} onChange={(e) => updateAddress('shipping_address', 'address_line2', e.target.value)} />
                      <div className="grid grid-cols-3" style={{ gap: '6px' }}>
                        <input type="text" className="form-input" style={{ fontSize: '13px' }} placeholder="City" value={formData.shipping_address?.city || ''} onChange={(e) => updateAddress('shipping_address', 'city', e.target.value)} />
                        <input type="text" className="form-input" style={{ fontSize: '13px' }} placeholder="State" value={formData.shipping_address?.state || ''} onChange={(e) => updateAddress('shipping_address', 'state', e.target.value)} />
                        <input type="text" className="form-input" style={{ fontSize: '13px' }} placeholder="Pincode" value={formData.shipping_address?.pincode || ''} onChange={(e) => updateAddress('shipping_address', 'pincode', e.target.value)} />
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Order Items</label>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '12px' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '4px' }}>SKU</th>
                          <th style={{ padding: '4px' }}>Name</th>
                          <th style={{ padding: '4px' }}>Item Code</th>
                          <th style={{ padding: '4px' }}>HSN</th>
                          <th style={{ padding: '4px' }}>Qty</th>
                          <th style={{ padding: '4px' }}>Unit</th>
                          <th style={{ padding: '4px' }}>Price</th>
                          <th style={{ padding: '4px' }}>GST%</th>
                          <th style={{ padding: '4px', textAlign: 'right' }}>Amount</th>
                          <th style={{ padding: '4px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.map((item, index) => {
                          const itemTotal = calculateItemTotals(item);
                          return (
                            <tr key={index}>
                              <td style={{ padding: '4px' }}>
                                <select className="form-select" value={item.sku} onChange={(e) => updateItem(index, 'sku', e.target.value)} required style={{ minWidth: '100px', fontSize: '12px', padding: '4px' }} data-testid={`item-sku-${index}`}>
                                  <option value="">Select...</option>
                                  {inventory.map((inv) => (<option key={inv.sku} value={inv.sku}>{inv.sku}</option>))}
                                </select>
                              </td>
                              <td style={{ padding: '4px' }}>
                                <input type="text" className="form-input" value={item.name} readOnly style={{ minWidth: '120px', fontSize: '12px', padding: '4px' }} data-testid={`item-name-${index}`} />
                              </td>
                              <td style={{ padding: '4px' }}>
                                <input type="text" className="form-input" value={item.item_code || ''} onChange={(e) => updateItem(index, 'item_code', e.target.value)} style={{ width: '90px', fontSize: '12px', padding: '4px' }} data-testid={`item-code-${index}`} />
                              </td>
                              <td style={{ padding: '4px' }}>
                                <input type="text" className="form-input" value={item.hsn_code} readOnly style={{ width: '80px', fontSize: '12px', padding: '4px' }} />
                              </td>
                              <td style={{ padding: '4px' }}>
                                <input type="number" className="form-input" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)} required style={{ width: '60px', fontSize: '12px', padding: '4px' }} data-testid={`item-qty-${index}`} />
                              </td>
                              <td style={{ padding: '4px' }}>
                                <select className="form-select" value={item.unit || 'Nos'} onChange={(e) => updateItem(index, 'unit', e.target.value)} style={{ width: '70px', fontSize: '12px', padding: '4px' }} data-testid={`item-unit-${index}`}>
                                  <option value="Nos">No's</option>
                                  <option value="Kgs">Kg's</option>
                                </select>
                              </td>
                              <td style={{ padding: '4px' }}>
                                <input type="number" step="0.01" className="form-input" value={item.unit_price} onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)} required style={{ width: '80px', fontSize: '12px', padding: '4px' }} />
                              </td>
                              <td style={{ padding: '4px' }}>
                                <input type="number" className="form-input" value={item.gst_rate} onChange={(e) => updateItem(index, 'gst_rate', parseFloat(e.target.value) || 0)} style={{ width: '50px', fontSize: '12px', padding: '4px' }} />
                              </td>
                              <td style={{ padding: '4px', textAlign: 'right' }} className="tabular-nums">{itemTotal.total_amount.toFixed(2)}</td>
                              <td style={{ padding: '4px' }}>
                                {formData.items.length > 1 && (
                                  <button type="button" onClick={() => removeItem(index)} className="btn btn-ghost btn-sm" data-testid={`remove-item-${index}`}><X size={14} color="#DC2626" /></button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex gap-2" style={{ marginTop: '8px' }}>
                    <button type="button" onClick={addItem} className="btn btn-secondary btn-sm" data-testid="add-item-btn">+ Add Item</button>
                    <button type="button" onClick={() => setShowQuickProduct(true)} className="btn btn-ghost btn-sm" style={{ fontSize: '11px' }} data-testid="quick-add-product"><Plus size={12} /> New SKU</button>
                  </div>
                </div>

                {formData.items.length > 0 && formData.items[0].sku && (
                  <div style={{ marginTop: '16px', padding: '16px', backgroundColor: 'var(--bg-surface-alt)', borderRadius: '4px' }}>
                    <div className="grid grid-cols-2">
                      <div>
                        <div style={{ fontSize: '13px', marginBottom: '4px' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Subtotal:</span>
                          <span style={{ float: 'right' }} className="tabular-nums">{totals.subtotal.toFixed(2)}</span>
                        </div>
                        <div style={{ fontSize: '13px', marginBottom: '4px' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>CGST:</span>
                          <span style={{ float: 'right' }} className="tabular-nums">{totals.cgstTotal.toFixed(2)}</span>
                        </div>
                        <div style={{ fontSize: '13px', marginBottom: '4px' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>SGST:</span>
                          <span style={{ float: 'right' }} className="tabular-nums">{totals.sgstTotal.toFixed(2)}</span>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: 700, textAlign: 'right' }}>
                          <span style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 500 }}>Total: </span>
                          <span className="tabular-nums">{totals.total.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Notes / Comments</label>
                  <textarea className="form-textarea" value={formData.notes} maxLength={NOTES_MAX} onChange={(e) => setFormData({ ...formData, notes: e.target.value.slice(0, NOTES_MAX) })} data-testid="notes-input" />
                  <div style={{ textAlign: 'right', fontSize: '11px', color: formData.notes.length >= NOTES_MAX ? '#DC2626' : 'var(--text-tertiary)' }} data-testid="notes-counter">
                    {formData.notes.length} / {NOTES_MAX} characters
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={closeOrderModal} className="btn btn-secondary" data-testid="cancel-btn">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-order-btn">{editingId ? 'Update Order' : 'Create Order'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showQuickCustomer && <QuickCreateCustomer onCreated={(c) => { setCustomers(prev => [...prev, c]); selectCustomer(c.id); }} onClose={() => setShowQuickCustomer(false)} />}
      {showQuickProduct && <QuickCreateProduct onCreated={(p) => { setInventory(prev => [...prev, p]); }} onClose={() => setShowQuickProduct(false)} />}

      {/* CoA Quick Create from Sales Order */}
      {showCoaModal && (
        <div className="modal-overlay" data-testid="coa-quick-modal">
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Create CoA {coaOrder ? `for ${coaOrder.order_number}` : ''}</h2>
            </div>
            <form onSubmit={submitCoaFromOrder}>
              <div className="modal-body">
                {coaVersionInfo?.found && (
                  <div style={{ background: '#e8f5e9', border: '1px solid #4caf50', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px' }} data-testid="coa-version-info">
                    Existing CoA found (v{coaVersionInfo.version}) for this Customer + Product. Data pre-filled. New save = <strong>v{coaVersionInfo.version + 1}</strong>.
                  </div>
                )}
                <div className="grid grid-cols-2" style={{ gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Customer</label>
                    <input type="text" className="form-input" value={coaForm.customer_name} readOnly style={{ background: 'var(--bg-secondary)' }} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Product (SKU) *</label>
                    <select className="form-select" value={coaForm.sku} onChange={(e) => handleCoaSkuChange(e.target.value)} required data-testid="coa-sku-select">
                      <option value="">Select Product...</option>
                      {coaOrder?.items?.map(i => <option key={i.sku} value={i.sku}>{i.sku} - {i.name}</option>)}
                      <optgroup label="All Products">
                        {inventory.map(i => <option key={i.sku} value={i.sku}>{i.sku} - {i.name}</option>)}
                      </optgroup>
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Product Name</label><input type="text" className="form-input" value={coaForm.product_name} onChange={(e) => setCoaForm({ ...coaForm, product_name: e.target.value })} /></div>
                  <div className="form-group"><label className="form-label">Size</label><input type="text" className="form-input" value={coaForm.product_size} onChange={(e) => setCoaForm({ ...coaForm, product_size: e.target.value })} placeholder="e.g. 475x620 mm" /></div>
                  <div className="form-group"><label className="form-label">Item Code</label><input type="text" className="form-input" value={coaForm.item_code} onChange={(e) => setCoaForm({ ...coaForm, item_code: e.target.value })} /></div>
                  <div className="form-group"><label className="form-label">Batch Number *</label><input type="text" className="form-input" value={coaForm.batch_number} onChange={(e) => setCoaForm({ ...coaForm, batch_number: e.target.value })} required data-testid="coa-batch" /></div>
                  <div className="form-group"><label className="form-label">Mfg. Date</label><input type="text" className="form-input" value={coaForm.mfg_date} onChange={(e) => setCoaForm({ ...coaForm, mfg_date: e.target.value })} placeholder="MM/YYYY" /></div>
                  <div className="form-group"><label className="form-label">Expiry Date</label><input type="text" className="form-input" value={coaForm.expiry_date} onChange={(e) => setCoaForm({ ...coaForm, expiry_date: e.target.value })} placeholder="MM/YYYY" /></div>
                  <div className="form-group"><label className="form-label">Quantity</label><input type="text" className="form-input" value={coaForm.quantity_text} onChange={(e) => setCoaForm({ ...coaForm, quantity_text: e.target.value })} /></div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0 8px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, margin: 0 }}>Test Parameters</h3>
                  <button type="button" onClick={addCoaTestRow} className="btn btn-secondary btn-sm"><Plus size={12} /> Add Row</button>
                </div>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={{ padding: '6px', width: '50px' }}>S.No</th><th style={{ padding: '6px' }}>Test</th><th style={{ padding: '6px' }}>Specification</th><th style={{ padding: '6px' }}>Result</th><th style={{ padding: '6px', width: '40px' }}></th></tr></thead>
                  <tbody>
                    {coaForm.test_rows.map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '4px', textAlign: 'center' }}>{row.sno}</td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.test} onChange={(e) => updateCoaTestRow(idx, 'test', e.target.value)} style={{ fontSize: '12px', padding: '4px 8px' }} /></td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.specification} onChange={(e) => updateCoaTestRow(idx, 'specification', e.target.value)} style={{ fontSize: '12px', padding: '4px 8px' }} /></td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.result} onChange={(e) => updateCoaTestRow(idx, 'result', e.target.value)} style={{ fontSize: '12px', padding: '4px 8px' }} /></td>
                        <td style={{ padding: '4px' }}>{coaForm.test_rows.length > 1 && <button type="button" onClick={() => removeCoaTestRow(idx)} className="btn btn-ghost btn-sm"><X size={12} color="red" /></button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="grid grid-cols-2" style={{ gap: '12px', marginTop: '16px' }}>
                  <div className="form-group"><label className="form-label">Prepared by</label><select className="form-select" value={coaForm.prepared_by} onChange={(e) => setCoaForm({ ...coaForm, prepared_by: e.target.value })}><option value="">Select User...</option>{users.map(u => <option key={u.id} value={u.name}>{u.name} ({u.email})</option>)}</select></div>
                  <div className="form-group"><label className="form-label">Approved by</label><select className="form-select" value={coaForm.approved_by} onChange={(e) => setCoaForm({ ...coaForm, approved_by: e.target.value })}><option value="">Select User...</option>{users.map(u => <option key={u.id} value={u.name}>{u.name} ({u.email})</option>)}</select></div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowCoaModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-coa-from-so">Create CoA</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesOrders;
